import { EmptyState, StatusDot } from "@/components/ui";
import type { Deployment, DeploymentEvent } from "@/types/api";
import {
  STEP_LABEL,
  STEP_ORDER,
  eventDot,
  formatDuration,
} from "@/lib/deployments";

/**
 * Vertical step-by-step progress for a single deployment run. Shared by the
 * tenant dashboard (inline, live) and the full history page. Renders steps in
 * canonical order, appending any unknown step the workflow emits so we never
 * silently drop progress.
 */
export function DeploymentTimeline({ deployment }: { deployment: Deployment }) {
  // Map by step name; last event wins (a terminal "succeeded" replaces an
  // earlier "started" — forward-compat safety net, the workflow only sends
  // terminal events today).
  const byStep: Record<string, DeploymentEvent> = {};
  for (const ev of deployment.events) {
    const existing = byStep[ev.step];
    if (!existing || ev.startedAt >= existing.startedAt) {
      byStep[ev.step] = ev;
    }
  }
  const knownSteps = STEP_ORDER.filter((s) => byStep[s]);
  const unknownSteps = Object.keys(byStep).filter(
    (s) => !STEP_ORDER.includes(s),
  );
  const orderedSteps = [...knownSteps, ...unknownSteps];

  if (orderedSteps.length === 0) {
    return (
      <EmptyState size="inline" title="No detailed steps yet.">
        Progress will appear here as soon as the first steps start.
      </EmptyState>
    );
  }

  return (
    <ol className="space-y-2">
      {orderedSteps.map((step) => {
        const ev = byStep[step];
        const dot = eventDot(ev.status);
        return (
          <li key={step} className="flex items-start gap-3 text-sm">
            <StatusDot tone={dot.tone} pulse={dot.pulse} size="lg" className="mt-1.5" />
            <div className="flex-1">
              <div className="flex items-baseline gap-2">
                <span className="font-medium text-ink">
                  {STEP_LABEL[step] ?? step}
                  {/* The dot is the only mark of the step's outcome: say it in words too. */}
                  <span className="sr-only"> — {ev.status}</span>
                </span>
                <span className="ml-auto text-xs text-ink-muted">
                  {formatDuration(ev.durationMs)}
                </span>
              </div>
              {ev.message && (
                <p className="mt-0.5 whitespace-pre-wrap break-words font-mono text-xs text-ink-muted">
                  {ev.message}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
